import * as React from "react"
import { Link as RouterLink, useLocation } from "react-router-dom"
import { Home, MessageSquare, Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import { HelpFeedbackModal } from "@/components/ui/help-feedback-modal"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { useAuth } from "@/lib/auth.tsx"
import { useBreadcrumbs } from '@/lib/useBreadcrumbs'
import { UserMenu } from "@/components/ui/user-menu"
import { cn } from '@/lib/utils'
import { UTMLink } from '@/components/custom-ui/utm-link'
import logoLarge from '@/assets/logo-large.png'

// Use UTMLink for public pages, RouterLink for authenticated pages
const Link = RouterLink

interface TopNavbarProps {
  showProfile?: boolean
  className?: string
  onMenuClick?: () => void
}

export function TopNavbar({ showProfile = true, className, onMenuClick }: TopNavbarProps) {
  const { session } = useAuth()
  const user = session?.user
  const location = useLocation()
  const breadcrumbs = useBreadcrumbs()
  const [helpModalOpen, setHelpModalOpen] = React.useState(false)

  return (
    <div className={cn("flex h-16 items-center bg-background border-b w-full z-50 sticky top-0", className)}>
      {/* Mobile Hamburger Menu Button - Only show for authenticated users */}
      {user && onMenuClick && (
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden ml-2 shrink-0"
          onClick={onMenuClick}
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {/* Mobile layout - centered text with logo on right */}
      {!user && (
        <>
          {/* Mobile: Centered text */}
          <div className="flex-1 md:hidden flex justify-center items-center px-3">
            <UTMLink to="/" className="font-semibold text-base hover:text-foreground/80 transition-colors">
              Rainfactory AI
            </UTMLink>
          </div>

          {/* Mobile: Logo on right */}
          <div className="md:hidden">
            <UTMLink to="/" className="flex items-center">
              <img src={logoLarge} alt="Rainfactory Logo" className="h-8 w-auto" />
            </UTMLink>
          </div>
        </>
      )}

      {/* Desktop layout - left side with logo, text, and nav (only for non-authenticated users) */}
      {!user && (
        <div className="hidden md:flex items-center gap-6 px-4">
          <UTMLink to="/" className="mr-8 font-semibold text-base hover:text-foreground/80 transition-colors flex items-center">
            <img src={logoLarge} alt="Rainfactory Logo" className="h-8 w-auto mr-2" />
            Rainfactory AI
          </UTMLink>
          {/* Navigation links */}
          <div className="flex items-center gap-6">
            <UTMLink
              to="/products"
              className={cn(
                "font-medium transition-colors hover:text-foreground/80",
                location.pathname === "/products" ? "text-foreground" : "text-foreground/60"
              )}
            >
              Product
            </UTMLink>
            <UTMLink
              to="/pricing"
              className={cn(
                "font-medium transition-colors hover:text-foreground/80",
                location.pathname === "/pricing" ? "text-foreground" : "text-foreground/60"
              )}
            >
              Pricing
            </UTMLink>
            <UTMLink
              to="/blog"
              className={cn(
                "font-medium transition-colors hover:text-foreground/80",
                location.pathname === "/blog" ? "text-foreground" : "text-foreground/60"
              )}
            >
              Blog
            </UTMLink>
            <UTMLink
              to="/about"
              className={cn(
                "font-medium transition-colors hover:text-foreground/80",
                location.pathname === "/about" ? "text-foreground" : "text-foreground/60"
              )}
            >
              About
            </UTMLink>
            <UTMLink
              to="/contact"
              className={cn(
                "font-medium transition-colors hover:text-foreground/80",
                location.pathname === "/contact" ? "text-foreground" : "text-foreground/60"
              )}
            >
              Contact
            </UTMLink>
          </div>
        </div>
      )}

      {/* Breadcrumbs for authenticated users - hidden on mobile */}
      {user && breadcrumbs.length > 0 && (
        <div className="hidden md:flex flex-1 min-w-0 overflow-hidden px-3 md:px-4 justify-start items-center">
          <Breadcrumb className="w-full flex">
            <BreadcrumbList className="flex items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5 min-w-0 justify-start">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <BreadcrumbItem className="min-w-0 shrink-0">
                    {crumb.current ? (
                      <BreadcrumbPage className="flex items-center">
                        {crumb.icon ? (
                          <Home size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                        ) : (
                          <span className="truncate text-sm font-medium max-w-[120px] md:max-w-[200px]" title={crumb.label}>
                            {crumb.label}
                          </span>
                        )}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href || "#"} className="flex items-center hover:text-foreground transition-colors">
                          {crumb.icon ? (
                            <Home size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                          ) : (
                            <span className="truncate text-sm max-w-[120px] md:max-w-[200px]" title={crumb.label}>
                              {crumb.label}
                            </span>
                          )}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && (
                    <BreadcrumbSeparator className="shrink-0 text-muted-foreground"> / </BreadcrumbSeparator>
                  )}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      )}

      {/* Spacer for authenticated users on mobile (when no breadcrumbs) */}
      {user && breadcrumbs.length === 0 && (
        <div className="flex-1 md:hidden"></div>
      )}

      {/* Right side - Help & Feedback and profile */}
      <div className="flex items-center gap-2 md:gap-4 shrink-0 pr-3 md:pr-4 ml-auto">
        {user && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHelpModalOpen(true)}
            className="flex items-center gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden md:inline">Help & Feedback</span>
          </Button>
        )}
        {user && showProfile ? (
          <UserMenu />
        ) : !user ? (
          <div className="flex items-center gap-2 shrink-0">
            <UTMLink to="/login" className="hidden md:block">
              <Button 
                size="sm"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 border-0"
              >
                Log In
              </Button>
            </UTMLink>
          </div>
        ) : null}
      </div>

      {/* Help & Feedback Modal */}
      <HelpFeedbackModal open={helpModalOpen} onOpenChange={setHelpModalOpen} />
    </div>
  )
} 